import type { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getTokenExpiration,
  refreshUserToken,
  swallowThunkRejection,
} from '@rsos/sinatra';

const MINUTE = 60 * 1000;

export const EXPIRATION_THRESHOLD = 5 * MINUTE;

/**
 * What to do when the access token reaches the point where it would normally
 * be renewed. The caller decides rather than the hook, because the two portal
 * apps detect an unrenewable session by different predicates and neither
 * generalises the other.
 */
export type ExpiringAction =
  | { kind: 'refresh' }
  | { kind: 'skip' }
  | { kind: 'redirect'; to: string };

export interface UseRenewAccessTokenOptions {
  onExpiring?: ExpiringAction;
  expirationThreshold?: number;
}

interface StateWithAuthTokens {
  sinatra: {
    auth: {
      tokens?: {
        token?: string;
        refresh_token?: string;
      };
    };
  };
}

const DEFAULT_ON_EXPIRING: ExpiringAction = { kind: 'refresh' };

const useRenewAccessToken = ({
  onExpiring = DEFAULT_ON_EXPIRING,
  expirationThreshold = EXPIRATION_THRESHOLD,
}: UseRenewAccessTokenOptions = {}): { token: string | undefined } => {
  const dispatch = useDispatch<ThunkDispatch<unknown, unknown, AnyAction>>();

  const token = useSelector(
    (state: StateWithAuthTokens) => state.sinatra.auth.tokens?.token,
  );
  const refreshToken = useSelector(
    (state: StateWithAuthTokens) => state.sinatra.auth.tokens?.refresh_token,
  );

  const isRenewingRef = useRef(false);

  const renew = useCallback(async () => {
    if (isRenewingRef.current || !refreshToken) {
      return;
    }
    isRenewingRef.current = true;
    try {
      // The thunk rather than the writer beneath it: the writer persists to
      // storage only, so redux would keep the stale token.
      await dispatch(refreshUserToken(refreshToken));
    } catch {
      swallowThunkRejection();
    } finally {
      isRenewingRef.current = false;
    }
  }, [dispatch, refreshToken]);

  // Primitives, because `onExpiring` is an object literal at the call sites and
  // its identity changes every render.
  const expiringKind = onExpiring.kind;
  const redirectTo = onExpiring.kind === 'redirect' ? onExpiring.to : null;

  useEffect(() => {
    if (expiringKind === 'skip') {
      return undefined;
    }

    // From the token, not from `auth.tokens.tokenExpiration`: authReducer does
    // not persist that field, so it is null on every page load and scheduling
    // against it refreshes on arrival whether or not the token needs it.
    const expiresAt = getTokenExpiration(token);

    // A redirect evicts rather than renews, so the pre-emptive buffer would
    // throw the user out five minutes early. Unreadable expiry means a non-JWT
    // auto-login token: nothing to infer, so treat it as due now.
    const dueAt = expiresAt
      ? expiresAt - (expiringKind === 'redirect' ? 0 : expirationThreshold)
      : 0;
    const delay = Math.max(0, dueAt - Date.now());

    const timerId = setTimeout(() => {
      if (redirectTo !== null) {
        window.location.assign(redirectTo);
        return;
      }
      void renew();
    }, delay);

    return () => clearTimeout(timerId);
    // `token` drives the reschedule: a successful refresh writes a new one.
  }, [token, expirationThreshold, expiringKind, redirectTo, renew]);

  return { token };
};

export default useRenewAccessToken;
